import type { Vec2, Vec3 } from '../geo/types';
import type { Pose, Transition } from './types';
import { CAMERA, FLY_ENTRY, WORLD } from '../core/config';
import { clamp, easeInOutCubic, lerp, wrapAngle } from '../core/math';
import { layoutToWorld, worldToLayout } from '../core/coords';

const RAD_TO_DEG = 180 / Math.PI;
const DEG_TO_RAD = Math.PI / 180;
const ENTRY_PITCH = FLY_ENTRY.pitchDeg * DEG_TO_RAD;

/** The view axis' direction over the ground, in layout metres: yaw is a compass bearing. */
function forwardOf(yaw: number): Vec2 {
  return [-Math.sin(yaw), Math.cos(yaw)];
}

/** The ground point a pose looks at, in layout coordinates; null where it looks at or above the sky. */
export function viewFocus(pose: Pose): Vec2 | null {
  if (pose.pitch >= 0) return null;
  const reach = (pose.position[1] - WORLD.groundY) / Math.tan(-pose.pitch);
  const [x, y] = worldToLayout(pose.position);
  const [fx, fy] = forwardOf(pose.yaw);
  return [x + fx * reach, y + fy * reach];
}

function groundFocus(pose: Pose): Vec3 | null {
  const focus = viewFocus(pose);
  return focus ? layoutToWorld(focus, WORLD.groundY) : null;
}

/**
 * Where the entry glide ends: over the ground the far view was looking at, at `FLY_ENTRY.altitudeM`
 * and its pitch, with the default lens. The camera keeps the painter's bearing, so the glide reads as
 * one move in along the view the painter left, and the city grows in the frame instead of shrinking.
 */
export function flyEntryTarget(from: Pose, spawn: Vec2): Pose {
  const focus = viewFocus(from) ?? spawn;
  const reach = (FLY_ENTRY.altitudeM - WORLD.groundY) / Math.tan(-ENTRY_PITCH);
  const [fx, fy] = forwardOf(from.yaw);
  return {
    position: layoutToWorld([focus[0] - fx * reach, focus[1] - fy * reach], FLY_ENTRY.altitudeM),
    yaw: from.yaw,
    pitch: ENTRY_PITCH,
    roll: 0,
    fovYDeg: CAMERA.defaultFovYDeg,
  };
}

/** The glide into fly mode from a pose far outside the city, such as the painter view. */
export function createFlyEntry(from: Pose, to: Pose): Transition {
  return {
    from,
    to,
    elapsed: 0,
    duration: FLY_ENTRY.durationS,
    targetMode: 'fly',
    endsAtPainterView: false,
    glide: { fromFocus: groundFocus(from), toFocus: groundFocus(to) },
  };
}

function distance(a: Vec3, b: Vec3): number {
  return Math.hypot(a[0] - b[0], a[1] - b[1], a[2] - b[2]);
}

function lerp3(a: Vec3, b: Vec3, t: number): Vec3 {
  return [lerp(a[0], b[0], t), lerp(a[1], b[1], t), lerp(a[2], b[2], t)];
}

/** Half the frame's height, in metres, at the ground point the view looks at. */
export function apparentHalfHeight(pose: Pose, focus: Vec3): number {
  return distance(pose.position, focus) * Math.tan(pose.fovYDeg / RAD_TO_DEG / 2);
}

/*
 * Lerping the lens while the camera closed 3 km on the city widened it faster than the city grew: the
 * "zooms out weirdly" of 2026-09-27, which came back when the 2026-09-29 envelope round took
 * `flyEntry.ts` with it. Here the half-height of the frame at the moving focus goes geometrically from
 * one end's to the other's, and the lens follows from the distance to it, so the ground's apparent
 * size only grows.
 */
function glideFov(transition: Transition, u: number, position: Vec3): number {
  const { from, to, glide } = transition;
  if (!glide?.fromFocus || !glide.toFocus) return lerp(from.fovYDeg, to.fovYDeg, u);
  const s0 = apparentHalfHeight(from, glide.fromFocus);
  const s1 = apparentHalfHeight(to, glide.toFocus);
  const halfHeight = s0 * Math.pow(s1 / s0, u);
  const reach = distance(position, lerp3(glide.fromFocus, glide.toFocus, u));
  const fov = 2 * Math.atan(halfHeight / reach) * RAD_TO_DEG;
  return clamp(fov, Math.min(from.fovYDeg, to.fovYDeg), Math.max(from.fovYDeg, to.fovYDeg));
}

/* A straight, eased path: no arc lift, which would carry the camera away from the city first. */
export function interpolateFlyEntry(transition: Transition): Pose {
  const { from, to, elapsed, duration } = transition;
  const u = easeInOutCubic(clamp(elapsed / duration, 0, 1));
  if (u <= 0) return from;
  if (u >= 1) return to;
  const position = lerp3(from.position, to.position, u);
  return {
    position,
    yaw: from.yaw + wrapAngle(to.yaw - from.yaw) * u,
    pitch: lerp(from.pitch, to.pitch, u),
    roll: lerp(from.roll, to.roll, u),
    fovYDeg: glideFov(transition, u, position),
  };
}
